import React from "react";

import { errorMessages } from "../utils/validation";
import { getInputDateFormat } from "../utils/utility";
import { Payment } from "../store/payment/types";
import { SelectOptions, AutocompleteOptions } from "../assets/data/options";
import { User } from "../store/user/types";
import { useTranslation } from "react-i18next";

import {
  CommonSelect,
  CommonTextField,
  CountryAutocomplete,
  CommonCheckbox,
  CommonDatePicker,
  CommonPhoneInput,
  CommonAutocomplete,
} from "./index";

export const UserInfoField = ({
  id,
  values,
  errors,
  touched,
  setFieldValue,
  disableImmutable,
  className,
  disabled,
  noLabels,
  subkey,
}: any) => {
  //Hooks
  const { t, i18n } = useTranslation();

  //Helpers
  const shouldDisable = (key: string) => {
    return disabled || (disableImmutable && !User.updateFields.includes(key));
  };

  const setSubFieldValue = (id: string, subkey: any, value: any) => {
    if (subkey !== undefined) {
      let field = [...values[id]];
      field[subkey] = value;
      setFieldValue(id, field);
    }
  };

  const getCommonDatePickerWrapperClass = (key: string) => {
    const isValidationFailed = errors && touched && errors[key] && touched[key];
    const isEmpty = values[key] ? false : true;
    return (
      "common-date-picker" +
      (isValidationFailed
        ? "--error"
        : shouldDisable(key)
        ? "--disabled"
        : "") +
      " common-date-picker" +
      (isEmpty ? "--empty" : "")
    );
  };

  const getPlaceholder = (id: string) => {
    if (noLabels) {
      return null;
    }

    switch (id) {
      case "address":
        return t("Address");
      case "availableCash":
        return t("Available cash");
      case "addressCity":
        return t("City of residence");
      case "addressCountry":
        return t("Address country");
      case "addressZipcode":
        return t("Zipcode");
      case "birthCity":
        return t("Birth city");
      case "bondsCertificates":
        return t("Select...");
      case "cellphone":
        return t("+49 12 345678");
      case "citizenship":
        return t("Citizenship");
      case "cocNumber":
        return t("CoC");
      case "companyName":
        return t("Company Name");
      case "confirm_password":
        return t("Confirm password");
      case "default_payment":
        return t("Select...");
      case "email":
        return t("email@mail.de");
      case "employment":
        return t("Current occupation");
      case "experience":
        return t("Select...");
      case "firstName":
        return t("First name");
      case "gender":
        return t("Select...");
      case "highestEducation":
        return t("Highest education");
      case "idDocumentCountry":
        return t("ID document country");
      case "idDocumentNumber":
        return t("ID document number");
      case "idType":
        return t("ID type");
      case "lastName":
        return t("Last name");
      case "monthlySalary":
        return t("Monthly salary");
      case "occupationGroup":
        return t("Occupation group");
      case "passportIssuingAgency":
        return t("Issuing agency");
      case "password":
        return t("Password");
      case "politicallyExposed":
        return t("Select...");
      case "prevEmployment":
        return t("Previous occupation");
      case "taxNumber":
        return t("Tax number");
      case "transactionsPerYear":
        return t("Transactions/Year");
      case "volumePerYear":
        return t("Volume/Year");
      default:
        return null;
    }
  }

  const getInitialFocusedDate = (id: string) => {
    switch (id) {
      case "birthDate":
        let initFocusDate = new Date();
        initFocusDate.setFullYear(initFocusDate.getFullYear() - 50);
        return initFocusDate;
      default:
        return undefined;
    }
  }

  const getLabel = (id: string) => {
    if (noLabels) {
      return null;
    }

    switch (id) {
      case "birthDate":
        return t("Birth date");
      case "cellphone":
        return t("Cellphone number");
      case "default_payment":
        return t("Default payment method");
      case "email":
        return t("Email");
      case "experience":
        return t("Experience");
      case "gender":
        return t("Gender");
      case "notProvidingExperience":
        return t("I don't want to give any details");
      case "notUSTax":
        return t("I am not a US tax resident");
      case "politicallyExposed":
        return t("Are you a politically exponated person?");
      default:
        return null;
    }
  }

  const getOptions = (id: string) => {
    switch (id) {
      case "default_payment":
        const keys = Object.keys(new Payment(null));

        return !values.payment_accounts ? 
          [] :
          values.payment_accounts
            .filter((account: Payment) => {
              const errors = account.validate(keys, errorMessages);
              return errors && Object.keys(errors).length === 0;
            })
            .map((account: Payment) => {
              return { label: account.IBAN, value: account.id };
            });
      case "experience":
        return SelectOptions()["experience"];
      case "gender":
          return SelectOptions()["gender"];
      case "highestEducation":
        return AutocompleteOptions()["educationLevel"];
      case "idType":
        return SelectOptions()["idType"];
      case "occupationGroup":
        return AutocompleteOptions()["occupationGroup"];
      case "politicallyExposed":
        return SelectOptions()["yesNo"];
      default:
        return null;
    }
  }

  const getType = (id: string) => {
    switch (id) {
      case "availableCash":
      case "monthlySalary":
      case "transactionsPerYear":
      case "volumePerYear":
        return "number";
      case "confirm_password":
      case "password":
        return "password";
      default:
        return undefined;
    }
  }

  const getMaxDate = (id: string) => {
    switch (id) {
      case "birthDate":
        let maxDate = new Date();
        maxDate.setFullYear(maxDate.getFullYear() - 18);
        return maxDate;
      default:
        return new Date("2100-01-01");
    }
  }

  const getProcessedValues = (id: string, values: any) => {
    switch (id) {
      case "experience":
      case "transactionsPerYear":
      case "volumePerYear":
        return {
          ...values,
          [id]: !!values[id] && subkey !== undefined ? values[id][subkey] : undefined
        }
      default:
        return values;
    }
  }

  const getSetFieldValueWrapper = (id: string) => {
    switch (id) {
      case "availableCash":
      case "monthlySalary":
        return (id: string, value: number) => setFieldValue(id, Math.round(value));
      case "experience":
        return (id: string, value: number) => setSubFieldValue(id, subkey, value);
      case "transactionsPerYear":
      case "volumePerYear":
        return (id: string, value: number) => setSubFieldValue(id, subkey, Math.round(value));
      default:
        return setFieldValue;
    }
  }

  const shouldDisableFuture = (id: string) => {
    switch (id) {
      default:
        return false;
    }
  }

  const shouldDisablePast = (id: string) => {
    switch (id) {
      default:
        return false;
    }
  }

  //Options
  const disableFuture = shouldDisableFuture(id);
  const disablePast = shouldDisablePast(id);
  const initialFocusedDate = getInitialFocusedDate(id);
  const label = getLabel(id);
  const maxDate = getMaxDate(id);
  const placeholder = getPlaceholder(id);
  const options = getOptions(id);
  const type = getType(id);
  const setFieldValueWrapper = getSetFieldValueWrapper(id);
  const processedErrors = getProcessedValues(id, errors);
  const processedValues = getProcessedValues(id, values);

  switch (id) {
    case "address":
    case "availableCash":
    case "addressCity":
    case "addressZipcode":
    case "birthCity":
    case "cocNumber":
    case "companyName":
    case "confirm_password":
    case "email":
    case "employment":
    case "firstName":
    case "idDocumentNumber":
    case "lastName":
    case "monthlySalary":
    case "passportIssuingAgency":
    case "password":
    case "prevEmployment":
    case "taxNumber":
    case "transactionsPerYear":
    case "volumePerYear":
      return (
        <CommonTextField
          disabled={shouldDisable(id)}
          id={id}
          type={type}
          label={label}
          placeholder={placeholder}
          values={processedValues}
          errors={processedErrors}
          touched={touched}
          setFieldValue={setFieldValueWrapper}
          className={className}
        />
      );
    case "default_payment":
    case "experience":
    case "gender":
    case "idType":
    case "politicallyExposed":
      return (
        <CommonSelect
          disabled={shouldDisable(id)}
          id={id}
          label={label}
          placeholder={placeholder}
          values={processedValues}
          errors={processedErrors}
          touched={touched}
          setFieldValue={setFieldValueWrapper}
          options={options}
          className={className}
        />
      );
    case "cellphone":
      return (
        <CommonPhoneInput
          disabled={shouldDisable(id)}
          type="tel"
          id={id}
          placeholder={placeholder}
          label={label}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          className={className}
        />
      );
    case "birthDate":
      return (
        <CommonDatePicker
          className={className + " " + getCommonDatePickerWrapperClass(id)}
          disabled={shouldDisable(id)}
          id={id}
          values={values}
          errors={errors}
          touched={touched}
          label={label}
          setFieldValue={setFieldValue}
          maxDate={maxDate}
          disableFuture={disableFuture}
          disablePast={disablePast}
          openTo="year"
          views={["year", "month", "date"]}
          format={getInputDateFormat(i18n.language)}
          initialFocusedDate={initialFocusedDate}
        />
      );
    case "addressCountry":
    case "citizenship":
    case "idDocumentCountry":
      return (
        <CountryAutocomplete
          disabled={shouldDisable(id)}
          id={id}
          placeholder={placeholder}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          options={AutocompleteOptions()["countries"]}
          className={className}
        />
      );
    case "highestEducation":
    case "occupationGroup":
      return (
        <CommonAutocomplete
          disabled={shouldDisable(id)}
          id={id}
          placeholder={placeholder}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          options={options}
          className={className}
        />
      );
    case "notProvidingExperience":
    case "notUSTax":
      return (
        <CommonCheckbox
          id={id}
          disabled={shouldDisable(id)}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          label={label}
          className={className}
        />
      );
    default:
      return <></>;
  }
};
